import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", description: "", category_id: "", price: 0, image_url: "", is_available: true, is_veg: true, prep_time_min: 15, tags: [], addons: [] };
const emptyCat = { name: "", description: "", image_url: "", sort_order: 0 };

export default function MenuMgmt() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const [catOpen, setCatOpen] = useState(false);
  const [catEdit, setCatEdit] = useState(null);
  const [catForm, setCatForm] = useState(emptyCat);

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/menu"), api.get("/categories")]);
    setItems(m.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEdit(null);
    setForm({ ...empty, category_id: cats[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEdit(it);
    setForm({
      name: it.name, description: it.description || "", category_id: it.category_id,
      price: it.price, image_url: it.image_url || "", is_available: it.is_available,
      is_veg: it.is_veg, prep_time_min: it.prep_time_min || 15, tags: it.tags || [], addons: it.addons || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name || !form.category_id) return toast.error("Name & category required");
      if (edit) await api.put(`/menu/${edit.id}`, form);
      else await api.post("/menu", form);
      toast.success("Saved");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/menu/${id}`); toast.success("Deleted"); load();
  };

  // Category CRUD
  const openCatCreate = () => { setCatEdit(null); setCatForm(emptyCat); setCatOpen(true); };
  const openCatEdit = (c) => { setCatEdit(c); setCatForm({ name: c.name, description: c.description || "", image_url: c.image_url || "", sort_order: c.sort_order || 0 }); setCatOpen(true); };
  const saveCat = async () => {
    try {
      if (!catForm.name) return toast.error("Name required");
      if (catEdit) await api.put(`/categories/${catEdit.id}`, catForm);
      else await api.post("/categories", catForm);
      toast.success("Saved"); setCatOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const delCat = async (id) => {
    if (!window.confirm("Delete category? Items will keep their category id.")) return;
    await api.delete(`/categories/${id}`); load();
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      (map[it.category_id] = map[it.category_id] || []).push(it);
    });
    return map;
  }, [items]);

  return (
    <>
      <PageHeader
        eyebrow="Library"
        title="Menu management"
        subtitle="Curate every dish. Upload images, set prices, tag dietary info, toggle availability."
      />

      <Tabs defaultValue="items" data-testid="menu-tabs">
        <TabsList>
          <TabsTrigger value="items" data-testid="menu-tab-items">Items ({items.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="menu-tab-categories">Categories ({cats.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={openCreate} className="btn-primary" data-testid="menu-add-item-btn"><Plus size={16} weight="bold"/> Add item</Button>
          </div>
          {cats.map((c) => (
            <div key={c.id} className="mb-6">
              <div className="label-eyebrow mb-3">{c.name}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 stagger">
                {(grouped[c.id] || []).map((it) => (
                  <div key={it.id} className="tile p-3 flex gap-3" data-testid={`menu-item-${it.id}`}>
                    <div className="w-20 h-20 rounded-md bg-[var(--bg-secondary)] overflow-hidden shrink-0">
                      {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <div className="font-semibold truncate">{it.name}</div>
                        <div className="font-display font-extrabold text-[var(--brand)]">{fmtMoney(it.price)}</div>
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] line-clamp-2">{it.description}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
                          <span className={it.is_available ? "text-[var(--brand-2)]" : "text-[var(--text-secondary)]"}>{it.is_available ? "Available" : "Hidden"}</span>
                          <span>·</span>
                          <span>{it.is_veg ? "Veg" : "Non-veg"}</span>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-[var(--bg-secondary)]" data-testid={`menu-edit-${it.id}`}><PencilSimple size={14}/></button>
                          <button onClick={() => del(it.id)} className="p-1.5 rounded text-[var(--brand)] hover:bg-[var(--bg-secondary)]" data-testid={`menu-del-${it.id}`}><Trash size={14}/></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {(grouped[c.id] || []).length === 0 && <div className="text-xs text-[var(--text-secondary)] col-span-full">No items in this category.</div>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={openCatCreate} className="btn-primary" data-testid="cat-add-btn"><Plus size={16} weight="bold"/> Add category</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {cats.map((c) => (
              <div key={c.id} className="tile p-3 flex gap-3" data-testid={`cat-${c.id}`}>
                <div className="w-16 h-16 rounded-md bg-[var(--bg-secondary)] overflow-hidden shrink-0">
                  {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-[var(--text-secondary)]">Sort: {c.sort_order}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openCatEdit(c)} className="p-1.5 rounded hover:bg-[var(--bg-secondary)]" data-testid={`cat-edit-${c.id}`}><PencilSimple size={14}/></button>
                  <button onClick={() => delCat(c.id)} className="p-1.5 rounded text-[var(--brand)] hover:bg-[var(--bg-secondary)]" data-testid={`cat-del-${c.id}`}><Trash size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Item dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="menu-item-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit item" : "Add item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="menu-form-name"/>
              </div>
              <div>
                <Label className="label-eyebrow">Price</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="menu-form-price"/>
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1" data-testid="menu-form-category"><SelectValue placeholder="Choose…"/></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={3} data-testid="menu-form-desc"/>
            </div>
            <div>
              <Label className="label-eyebrow">Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1" data-testid="menu-form-image"/>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })} data-testid="menu-form-available"/>
                Available
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.is_veg} onCheckedChange={(v) => setForm({ ...form, is_veg: v })} data-testid="menu-form-veg"/>
                Vegetarian
              </label>
              <div className="flex items-center gap-2 text-sm">
                <Label>Prep min</Label>
                <Input type="number" value={form.prep_time_min} onChange={(e) => setForm({ ...form, prep_time_min: parseInt(e.target.value || 0) })} className="w-20" data-testid="menu-form-prep"/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="btn-primary" data-testid="menu-form-save">{edit ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{catEdit ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="mt-1" data-testid="cat-form-name"/>
            </div>
            <div>
              <Label className="label-eyebrow">Image URL</Label>
              <Input value={catForm.image_url} onChange={(e) => setCatForm({ ...catForm, image_url: e.target.value })} className="mt-1" data-testid="cat-form-image"/>
            </div>
            <div>
              <Label className="label-eyebrow">Sort order</Label>
              <Input type="number" value={catForm.sort_order} onChange={(e) => setCatForm({ ...catForm, sort_order: parseInt(e.target.value || 0) })} className="mt-1" data-testid="cat-form-sort"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCat} className="btn-primary" data-testid="cat-form-save">{catEdit ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
